import React, { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useLang } from "../context/LanguageContext";
import { useHours } from "../hooks/useHours";
import { useContact } from "../hooks/useContact";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = ({ embedded = false }) => {
  const { t, lang } = useLang();
  const { rows: hourRows } = useHours();
  const { contact } = useContact();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t.contact.form_error);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        subject: form.subject || null,
        message: form.message,
      });
      toast.success(t.contact.form_success);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.form_error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="contact-section"
      className={`relative ${embedded ? "py-24 lg:py-32" : "pt-32 pb-24 lg:pt-40 lg:pb-32"}`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6 lg:gap-12 mb-16">
          <div className="col-span-12 md:col-span-7">
            <p className="overline text-[color:var(--wine)] flex items-center">
              <span className="section-line"></span>
              {t.contact.eyebrow}
            </p>
            <h2 className="mt-6 font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              {t.contact.title}
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:pt-4">
            <p className="text-lg text-[color:var(--ink-2)] leading-relaxed">
              {t.contact.subtitle}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 lg:gap-12">
          <div className="col-span-12 lg:col-span-5 space-y-10">
            <div data-testid="contact-block-address">
              <p className="overline text-[color:var(--wine)]">
                {t.contact.address_label}
              </p>
              <p className="mt-3 font-serif-display text-3xl leading-tight flex items-start gap-2">
                <MapPin size={20} strokeWidth={1.2} className="mt-2 text-[color:var(--ink-3)]" />
                {contact.address}
              </p>
            </div>

            <div data-testid="contact-block-hours">
              <p className="overline text-[color:var(--wine)] flex items-center gap-2">
                <Clock size={12} strokeWidth={1.4} />
                {t.contact.hours_label}
              </p>
              <div className="mt-4 space-y-2">
                {hourRows.map((h) => (
                  <div
                    key={h.id}
                    className="flex items-baseline text-[color:var(--ink-2)] text-lg"
                  >
                    <span>{h[`label_${lang}`] || h.label_it}</span>
                    <span className="divider-dotted" />
                    <span>{h.time}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div data-testid="contact-block-phone">
                <p className="overline text-[color:var(--wine)]">
                  {t.contact.phone_label}
                </p>
                <a
                  href={`tel:${contact.phone.replace(/\s/g, "")}`}
                  className="mt-2 block font-serif-display text-2xl link-underline"
                >
                  {contact.phone}
                </a>
              </div>
              <div data-testid="contact-block-email">
                <p className="overline text-[color:var(--wine)]">
                  {t.contact.email_label}
                </p>
                <a
                  href={`mailto:${contact.email}`}
                  className="mt-2 block font-serif-display text-xl break-all link-underline"
                >
                  {contact.email}
                </a>
              </div>
            </div>

            <div className="aspect-[5/3] overflow-hidden border border-[color:var(--line)]">
              <iframe
                title="map"
                data-testid="contact-map"
                src={contact.maps_url}
                width="100%"
                height="100%"
                loading="lazy"
                style={{ border: 0, filter: "grayscale(0.4) contrast(0.95)" }}
              />
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="col-span-12 lg:col-span-7 bg-[color:var(--ivory-2)] p-6 lg:p-10"
          >
            <p className="overline text-[color:var(--ink-3)]">{t.contact.form_title}</p>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="c-name" className="overline text-[color:var(--ink-3)]">
                  {t.contact.form_name}
                </Label>
                <Input
                  id="c-name"
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={update("name")}
                  className="mt-2 rounded-none border-0 border-b border-[color:var(--ink-3)]/30 bg-transparent focus-visible:ring-0 focus-visible:border-[color:var(--wine)] px-0 text-lg"
                  required
                />
              </div>
              <div>
                <Label htmlFor="c-email" className="overline text-[color:var(--ink-3)]">
                  {t.contact.form_email}
                </Label>
                <Input
                  id="c-email"
                  type="email"
                  data-testid="contact-email-input"
                  value={form.email}
                  onChange={update("email")}
                  className="mt-2 rounded-none border-0 border-b border-[color:var(--ink-3)]/30 bg-transparent focus-visible:ring-0 focus-visible:border-[color:var(--wine)] px-0 text-lg"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="c-subject" className="overline text-[color:var(--ink-3)]">
                  {t.contact.form_subject}
                </Label>
                <Input
                  id="c-subject"
                  data-testid="contact-subject-input"
                  value={form.subject}
                  onChange={update("subject")}
                  className="mt-2 rounded-none border-0 border-b border-[color:var(--ink-3)]/30 bg-transparent focus-visible:ring-0 focus-visible:border-[color:var(--wine)] px-0 text-lg"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="c-message" className="overline text-[color:var(--ink-3)]">
                  {t.contact.form_message}
                </Label>
                <Textarea
                  id="c-message"
                  data-testid="contact-message-textarea"
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  className="mt-2 rounded-none border-0 border-b border-[color:var(--ink-3)]/30 bg-transparent focus-visible:ring-0 focus-visible:border-[color:var(--wine)] px-0 text-base resize-none"
                  required
                />
              </div>
            </div>
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={submitting}
              className="mt-10 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? t.contact.form_submitting : t.contact.form_submit}
              <Send size={14} strokeWidth={1.5} />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
